import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { getFileName, getFileIcon } from '../utils/fileUtils';
import './FileSummary.css';

/**
 * Component for displaying file summary information
 * @param {Object} props - Component props
 * @param {Object} props.file - File data
 * @param {boolean} props.showControls - Whether to show action controls
 */
function FileSummary({ file, showControls }) {
  // Generate display data
  const fileName = getFileName(file.path);
  const fileExtension = fileName.split('.').pop();
  const fileIcon = getFileIcon(file.language);
  
  // Format metrics
  const formatNumber = (num) => {
    return typeof num === 'number' ? num.toLocaleString() : 'N/A';
  };
  
  return (
    <div className="file-summary">
      <div className="file-summary-header">
        <div className="file-icon-container">
          <span className="file-icon">{fileIcon}</span>
          <span className="file-extension">{fileExtension}</span>
        </div>
        
        <div className="file-title">
          <h3 className="file-name">{fileName}</h3>
          <div className="file-path">{file.path}</div>
        </div>
        
        {showControls && (
          <div className="file-controls">
            <Link 
              to={`/project/${file.projectId}/file/${file.id}`}
              className="file-control view-file"
              title="View file"
            >
              <span className="control-icon">📄</span>
            </Link>
            <Link 
              to={`/project/${file.projectId}/file/${file.id}/history`}
              className="file-control view-history"
              title="View history"
            >
              <span className="control-icon">📋</span>
            </Link>
          </div>
        )}
      </div>
      
      <div className="file-metrics">
        <div className="metric">
          <span className="metric-label">Language</span>
          <span className="metric-value">{file.language || fileExtension}</span>
        </div>
        <div className="metric">
          <span className="metric-label">LOC</span>
          <span className="metric-value">{formatNumber(file.actualLoc || file.estimatedLoc)}</span>
        </div>
        {file.exports && file.exports.length > 0 && (
          <div className="metric">
            <span className="metric-label">Exports</span>
            <span className="metric-value">{formatNumber(file.exports.length)}</span>
          </div>
        )}
        {file.imports && file.imports.length > 0 && (
          <div className="metric">
            <span className="metric-label">Imports</span>
            <span className="metric-value">{formatNumber(file.imports.length)}</span>
          </div>
        )}
      </div>
      
      {(file.description || (file.exports && file.exports.length > 0)) && (
        <div className="file-details">
          {file.description && (
            <div className="file-description">
              {file.description}
            </div>
          )}
          
          {file.exports && file.exports.length > 0 && (
            <div className="file-exports">
              <span className="exports-label">Exports:</span>
              <div className="exports-list">
                {file.exports.map((exp, index) => (
                  <span key={index} className="export-item">
                    {exp}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

FileSummary.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    projectId: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    language: PropTypes.string,
    estimatedLoc: PropTypes.number,
    actualLoc: PropTypes.number,
    description: PropTypes.string,
    exports: PropTypes.array,
    imports: PropTypes.array
  }).isRequired,
  showControls: PropTypes.bool
};

FileSummary.defaultProps = {
  showControls: true
};

export default FileSummary;
